import { z } from "zod";
import { assetKeySchema, colorSchema } from "./primitives.js";
import { bookendTextDefaults, textStyleSchema } from "./text.js";

/**
 * A card is a canvas.
 *
 * It was a title and a subtitle in a fixed stack, which is why every card ever
 * made looks the same. An element carries its own box, so text goes where it is
 * wanted — and because a card written before this has to keep rendering exactly
 * as it did, the old two lines are read into two elements at the positions the
 * stack put them in rather than migrated into a new arrangement.
 */

/**
 * Where an element sits against the card.
 *
 * Nine named points and `free`. The named ones are an anchor rather than a
 * snap: the box is positioned from the anchored edge every time it is drawn, so
 * a line set to the right stays against the right as it grows instead of
 * walking off the card. `free` is what dragging produces — the box was put
 * somewhere deliberately, and nothing should move it afterwards.
 */
export const cardAnchors = [
  "top-left",
  "top-center",
  "top-right",
  "middle-left",
  "middle-center",
  "middle-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
  "free",
] as const;

export type CardAnchor = (typeof cardAnchors)[number];

/**
 * How far an anchored element keeps from the card's edge, as a fraction of the
 * frame. The card's own `padding: 0 10%` — so an anchored element lines up with
 * where the stack always sat.
 */
export const cardMargin = 0.1;

/** The height the composition-pixel font sizes are quoted against. */
const REFERENCE_HEIGHT = 1080;

/** The gap the old stack left between its two lines. */
const STACK_GAP = 22 / REFERENCE_HEIGHT;

/**
 * How an element arrives and leaves.
 *
 * Optional, and absent by default, because absent means something: an element
 * with no motion of its own moves with the card's own Entrance. That keeps a
 * card nobody has touched behaving exactly as it did, and leaves one rule
 * rather than two competing ones.
 *
 * `rise` and `slide` stay fully opaque on the way in, so they are legible on
 * frame zero — the frame a player shows as the thumbnail, and the frame this
 * codebase has already shipped blank once. `fade` cannot be, and neither can
 * any delay; the panel says so rather than letting it be discovered on export.
 */
export const cardMotionTypes = ["none", "fade", "rise", "slide"] as const;
export type CardMotionType = (typeof cardMotionTypes)[number];

const motionStepSchema = z.object({
  type: z.enum(cardMotionTypes).default("none"),
  duration: z.number().min(0).max(3).default(0.5),
});

export const cardMotionSchema = z.object({
  /** Seconds before the element appears at all. What makes a title build. */
  delay: z.number().min(0).max(5).default(0),
  entry: motionStepSchema.default({ type: "rise", duration: 0.5 }),
  exit: motionStepSchema.default({ type: "none", duration: 0.5 }),
});

/** What an element does when nothing has said otherwise: arrives, stays. */
export const defaultCardMotion: CardMotion = {
  delay: 0,
  entry: { type: "rise", duration: 0.5 },
  exit: { type: "none", duration: 0.5 },
};

export type CardMotion = z.infer<typeof cardMotionSchema>;

/** The box and behaviour every element kind shares. */
/**
 * A shadow under one element, as four numbers and a colour.
 *
 * Offsets and blur are composition pixels in the fixed 1920-wide space, like a
 * font size, so a card casts the same shadow at 720p and at 4K.
 *
 * No spread and no inset, and that is a constraint rather than an omission.
 * The browser exporter re-implements CSS painting: its shadow parser reads
 * offset, offset and blur, discards the fourth value, and strips the `inset`
 * keyword before parsing — so a spread would quietly come out small and an
 * inset shadow would be drawn *outside* the element. Rendering in a browser is
 * the free path, so an effect that only survives the worker is one that pushes
 * a card onto the paid one, or exports looking different from the preview.
 */
export const cardShadowSchema = z.object({
  x: z.number().min(-200).max(200).default(0),
  y: z.number().min(-200).max(200).default(10),
  blur: z.number().min(0).max(200).default(24),
  color: colorSchema.default("#10151c"),
  opacity: z.number().min(0).max(1).default(0.24),
});

export type CardShadow = z.infer<typeof cardShadowSchema>;

/**
 * The five on offer, and the reason there are five rather than a blank slider.
 *
 * A shadow is four numbers, and most of the combinations look like a mistake.
 * These are the answer to "make this look right"; the numbers stay reachable
 * for somebody who already knows what they want.
 *
 * `edge` has no blur at all — a hard offset, which reads as a deliberate
 * graphic rather than as depth, and is the one that survives being printed.
 */
export const cardShadowPresets = {
  soft: { x: 0, y: 2, blur: 8, color: "#10151c", opacity: 0.16 },
  lifted: { x: 0, y: 10, blur: 24, color: "#10151c", opacity: 0.24 },
  deep: { x: 0, y: 22, blur: 44, color: "#10151c", opacity: 0.32 },
  edge: { x: 6, y: 6, blur: 0, color: "#10151c", opacity: 0.85 },
} as const satisfies Record<string, CardShadow>;

export type CardShadowPreset = keyof typeof cardShadowPresets;

/** Which preset a shadow matches, or null for "none" and for hand-tuned values. */
export function matchShadowPreset(shadow: CardShadow | undefined): CardShadowPreset | null {
  if (!shadow) return null;
  for (const [name, preset] of Object.entries(cardShadowPresets)) {
    const same =
      preset.x === shadow.x &&
      preset.y === shadow.y &&
      preset.blur === shadow.blur &&
      preset.color.toLowerCase() === shadow.color.toLowerCase() &&
      Math.abs(preset.opacity - shadow.opacity) < 0.001;
    if (same) return name as CardShadowPreset;
  }
  return null;
}

const elementBase = {
  id: z.string().min(1).max(64),
  anchor: z.enum(cardAnchors).default("middle-center"),
  /** Fractions of the frame, like a focus target — so a card composes the same at 720p and 4K. */
  x: z.number().min(-0.5).max(1.5).default(cardMargin),
  y: z.number().min(-0.5).max(1.5).default(0.4),
  width: z
    .number()
    .min(0.02)
    .max(2)
    .default(1 - cardMargin * 2),
  height: z.number().min(0.02).max(2).default(0.2),
  /**
   * Required, with a default. There used to be a card-wide `entrance` that an
   * element with no motion inherited; presets write motion onto every element
   * directly, so the inherited default had nothing left to do and an absent
   * value no longer means anything. Every element describes its own movement.
   */
  motion: cardMotionSchema.default({}),
  /**
   * Absent means no shadow, which is what every existing card has and what
   * most cards should keep. Optional rather than defaulted for that reason: a
   * default would change how every card already made is drawn.
   */
  shadow: cardShadowSchema.optional(),
};

export const cardTextElementSchema = z.object({
  ...elementBase,
  kind: z.literal("text"),
  text: z.string().max(500).default(""),
  /**
   * Which pair of weights `bold` picks between, and what an untouched element
   * is drawn at. A title's bold is 800 and a subtitle's is 700: they were never
   * the same, and a migrated card would change appearance if this were dropped.
   */
  role: z.enum(["title", "subtitle"]).default("title"),
  anchor: z.enum(cardAnchors).default("middle-center"),
  /** Fractions of the frame, like a focus target — so a card composes the same at 720p and 4K. */
  x: z.number().min(-0.5).max(1.5).default(cardMargin),
  y: z.number().min(-0.5).max(1.5).default(0.4),
  width: z
    .number()
    .min(0.02)
    .max(2)
    .default(1 - cardMargin * 2),
  height: z.number().min(0.02).max(2).default(0.2),
  /** Where the words sit inside the box, which is not where the box sits on the card. */
  align: z.enum(["left", "center", "right"]).default("center"),
  style: textStyleSchema.optional(),
});

export const cardImageElementSchema = z.object({
  ...elementBase,
  kind: z.literal("image"),
  /**
   * Absent until one is chosen. A sentinel key pointing at a file nobody
   * uploaded is a broken image in the render and an unanswerable question in
   * the panel; nothing is honestly nothing.
   */
  assetKey: assetKeySchema.optional(),
  fit: z.enum(["cover", "contain"]).default("contain"),
  radius: z.number().min(0).max(200).default(0),
  opacity: z.number().min(0).max(1).default(1),
});

export const cardShapes = ["rect", "ellipse", "line", "arrow"] as const;
export type CardShape = (typeof cardShapes)[number];

export const cardShapeElementSchema = z.object({
  ...elementBase,
  kind: z.literal("shape"),
  shape: z.enum(cardShapes).default("rect"),
  fill: colorSchema.default("#38bdf8"),
  stroke: colorSchema.default("#38bdf8"),
  /** Composition pixels, in the fixed 1920-wide space, like a font size. */
  strokeWidth: z.number().min(0).max(60).default(0),
  radius: z.number().min(0).max(200).default(12),
  opacity: z.number().min(0).max(1).default(1),
});

/**
 * Paint order is array order, so a shape written before a line of text sits
 * behind it. One rule, and it is the one every drawing tool already uses.
 */
export const cardElementSchema = z.discriminatedUnion("kind", [
  cardTextElementSchema,
  cardImageElementSchema,
  cardShapeElementSchema,
]);

export type CardTextElement = z.infer<typeof cardTextElementSchema>;
export type CardImageElement = z.infer<typeof cardImageElementSchema>;
export type CardShapeElement = z.infer<typeof cardShapeElementSchema>;
export type CardElement = z.infer<typeof cardElementSchema>;

export interface CardBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where an element is actually drawn.
 *
 * Resolved on every read rather than written into the element, which is what
 * makes the anchor hold: widen a right-anchored box and its right edge has not
 * moved, because the left edge was never the thing being stored.
 */
export function anchoredBox(element: CardElement): CardBox {
  const { x, y, width, height, anchor } = element;
  if (anchor === "free") return { x, y, width, height };

  const [vertical, horizontal] = anchor.split("-");
  return {
    width,
    height,
    x: horizontal === "left" ? cardMargin : horizontal === "right" ? 1 - cardMargin - width : (1 - width) / 2,
    y: vertical === "top" ? cardMargin : vertical === "bottom" ? 1 - cardMargin - height : (1 - height) / 2,
  };
}

/** The gap stacked elements leave between them, as a fraction of the frame. */
const ANCHOR_STACK_GAP = 0.022;

/**
 * Where every element on a card is drawn, resolved together.
 *
 * An anchor names a point, and two elements pinned to the same point would land
 * in the same place — a card with its subtitle printed through its title. So
 * anchored elements sharing a point are stacked around it: downwards from a top
 * edge, upwards from a bottom one, centred as a group in the middle. A tall
 * stack is pulled back onto the card rather than allowed to run off it.
 *
 * Card-level rather than per-element because that is the only place the answer
 * exists: no element can know what else is pinned where it is. `anchoredBox` is
 * still right for a single element on its own, and this agrees with it whenever
 * an anchor is used once.
 */
export function anchoredBoxes(elements: readonly CardElement[]): CardBox[] {
  const boxes = elements.map((element) => anchoredBox(element));
  const groups = new Map<CardAnchor, number[]>();
  elements.forEach((element, index) => {
    if (element.anchor === "free") return;
    groups.set(element.anchor, [...(groups.get(element.anchor) ?? []), index]);
  });

  for (const [anchor, indices] of groups) {
    if (indices.length < 2) continue;
    const heights = indices.map((index) => elements[index].height);
    const total = heights.reduce((sum, height) => sum + height, 0) + ANCHOR_STACK_GAP * (indices.length - 1);
    const vertical = anchor.split("-")[0];

    let cursor = vertical === "top" ? cardMargin : vertical === "bottom" ? 1 - cardMargin - total : (1 - total) / 2;
    cursor = Math.max(0, Math.min(cursor, Math.max(0, 1 - total)));

    indices.forEach((index, position) => {
      boxes[index] = { ...boxes[index], y: Number(cursor.toFixed(5)) };
      cursor += heights[position] + ANCHOR_STACK_GAP;
    });
  }

  return boxes;
}

/**
 * The element under a point on the card, or null for bare card.
 *
 * Boxes are resolved card-wide rather than per element, because two things on
 * the same anchor are stacked apart when they are drawn — hit-testing the
 * unstacked box would pick at a point where nothing is.
 *
 * Searched back to front: elements are painted in array order with no z-index,
 * so the last one drawn is the one a click lands on.
 */
export function elementAt(elements: readonly CardElement[], point: { x: number; y: number }): CardElement | null {
  const boxes = anchoredBoxes(elements);
  for (let index = elements.length - 1; index >= 0; index -= 1) {
    const box = boxes[index];
    if (point.x >= box.x && point.x <= box.x + box.width && point.y >= box.y && point.y <= box.y + box.height) {
      return elements[index];
    }
  }
  return null;
}

/** Where the words sit vertically inside their box, which follows the anchor. */
export function anchoredJustify(element: CardElement): "flex-start" | "center" | "flex-end" {
  if (element.anchor.startsWith("top")) return "flex-start";
  if (element.anchor.startsWith("bottom")) return "flex-end";
  return "center";
}

let sequence = 0;

/** A new text element, in the middle of the card where an insert should land. */
export function newCardText(text = "", role: "title" | "subtitle" = "title"): CardTextElement {
  sequence += 1;
  const fontSize = bookendTextDefaults[role].fontSize;
  return {
    id: `txt_${Date.now().toString(36)}_${sequence.toString(36)}`,
    kind: "text",
    text,
    role,
    anchor: "middle-center",
    x: cardMargin,
    y: 0.4,
    width: 1 - cardMargin * 2,
    height: lineHeight(role, fontSize),
    align: "center",
    style: undefined,
    motion: { ...defaultCardMotion },
  };
}

/** A new picture, waiting for one to be chosen. */
export function newCardImage(): CardImageElement {
  return { ...box("img"), kind: "image", fit: "contain", radius: 0, opacity: 1, motion: { ...defaultCardMotion } };
}

/** A new shape, big enough to see and quiet enough not to shout. */
export function newCardShape(shape: CardShape = "rect"): CardShapeElement {
  const line = shape === "line" || shape === "arrow";
  return {
    ...box("shp"),
    kind: "shape",
    shape,
    fill: line ? "#00000000" : "#38bdf8",
    stroke: "#38bdf8",
    strokeWidth: line ? 6 : 0,
    motion: { ...defaultCardMotion },
    radius: shape === "rect" ? 12 : 0,
    opacity: line ? 1 : 0.9,
    height: line ? 0.04 : 0.24,
    width: line ? 0.5 : 0.3,
  };
}

/** The shared half of a new element: its identity and its starting box. */
function box(prefix: string): {
  id: string;
  anchor: CardAnchor;
  x: number;
  y: number;
  width: number;
  height: number;
} {
  sequence += 1;
  return {
    id: `${prefix}_${Date.now().toString(36)}_${sequence.toString(36)}`,
    anchor: "middle-center",
    x: cardMargin,
    y: 0.4,
    width: 1 - cardMargin * 2,
    height: 0.2,
  };
}

/** How tall one line of a role is, as a fraction of the frame. */
function lineHeight(role: "title" | "subtitle", fontSize: number): number {
  const leading = role === "title" ? 1.05 : 1.3;
  return (fontSize * leading) / REFERENCE_HEIGHT;
}

interface LegacyCard {
  title?: unknown;
  subtitle?: unknown;
  titleStyle?: { fontSize?: number } & Record<string, unknown>;
  subtitleStyle?: { fontSize?: number } & Record<string, unknown>;
  align?: unknown;
}

/**
 * The old two lines, at the positions the old stack drew them.
 *
 * The stack was centred as a group with a fixed gap, so a card with only a
 * title centred that title and a card with both centred the pair. Reproducing
 * that here is the difference between a migration nobody notices and every
 * existing card jumping the first time it is opened.
 *
 * They come out `free` rather than anchored, because that is what they are:
 * positions the old layout computed, not a corner anyone chose.
 */
function elementsFromLines(card: LegacyCard): Omit<CardTextElement, "motion">[] {
  const align = card.align === "left" ? "left" : "center";
  const title = typeof card.title === "string" ? card.title : "";
  const subtitle = typeof card.subtitle === "string" ? card.subtitle : "";

  const lines: { text: string; role: "title" | "subtitle"; style: unknown; height: number }[] = [];
  if (title) {
    const size = card.titleStyle?.fontSize ?? bookendTextDefaults.title.fontSize;
    lines.push({ text: title, role: "title", style: card.titleStyle, height: lineHeight("title", size) });
  }
  if (subtitle) {
    const size = card.subtitleStyle?.fontSize ?? bookendTextDefaults.subtitle.fontSize;
    lines.push({ text: subtitle, role: "subtitle", style: card.subtitleStyle, height: lineHeight("subtitle", size) });
  }
  if (lines.length === 0) return [];

  const total = lines.reduce((sum, line) => sum + line.height, 0) + STACK_GAP * (lines.length - 1);
  let cursor = (1 - total) / 2;

  return lines.map((line, index) => {
    const element: Omit<CardTextElement, "motion"> = {
      id: `txt_legacy_${index + 1}`,
      kind: "text",
      text: line.text,
      role: line.role,
      anchor: "free",
      x: cardMargin,
      y: Number(cursor.toFixed(5)),
      width: 1 - cardMargin * 2,
      height: Number(line.height.toFixed(5)),
      align,
      style: (line.style as CardTextElement["style"]) ?? undefined,
    };
    cursor += line.height + STACK_GAP;
    return element;
  });
}

/** Which preset a card came from, and whether it has been changed since. */
export const cardPresetRefSchema = z.object({
  id: z.string().min(1).max(40),
  /**
   * Set the moment something the preset owns is edited by hand. The gallery
   * keeps the name — “Custom, from Lower third” — rather than lighting a tile
   * that no longer describes what is on screen, which is the same honesty as an
   * anchor saying it was placed by hand.
   */
  edited: z.boolean().optional(),
});

export type CardPresetRef = z.infer<typeof cardPresetRefSchema>;

/** The motion the retired card-wide `entrance` described, per element. */
function motionFromEntrance(entrance: unknown): CardMotion {
  if (entrance === "fade")
    return { delay: 0, entry: { type: "fade", duration: 0.7 }, exit: { type: "none", duration: 0.5 } };
  if (entrance === "none")
    return { delay: 0, entry: { type: "none", duration: 0 }, exit: { type: "none", duration: 0.5 } };
  return { delay: 0, entry: { type: "rise", duration: 0.7 }, exit: { type: "none", duration: 0.5 } };
}

/**
 * Moves a card's retired `entrance` onto its elements.
 *
 * `entrance` was one control the whole card obeyed and every element inherited.
 * Presets write motion onto each element directly, so there is nothing left to
 * inherit — but deleting the field on its own would leave every card that
 * exists standing perfectly still, which is not what any of them were set to
 * do. So the value is read one last time on the way in and written where it now
 * belongs, and an element that already says how it moves is never overruled.
 *
 * At scene level rather than content level because that is where `entrance`
 * lived; the content's own migration runs first so there are elements to write
 * onto at all.
 */
export function withElementMotion(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const scene = value as Record<string, unknown>;
  const content = withCardElements(scene.content);
  if (!content || typeof content !== "object") return { ...scene, content };

  const card = content as Record<string, unknown>;
  if (card.kind !== "card" || !Array.isArray(card.elements)) return { ...scene, content };

  const motion = motionFromEntrance(scene.entrance);
  const elements = card.elements.map((element) =>
    element && typeof element === "object" && (element as Record<string, unknown>).motion === undefined
      ? { ...(element as Record<string, unknown>), motion }
      : element,
  );
  return { ...scene, content: { ...card, elements } };
}

/**
 * Fills in `elements` for a card that predates them.
 *
 * On read, so nothing has to be rewritten in storage before it renders and a
 * card that is never opened is never touched. A card that already has elements
 * is left exactly as it is — including one whose `title` is stale, which is
 * what happens the moment the first element is edited.
 */
export function withCardElements(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const card = value as Record<string, unknown>;
  if (card.kind !== "card" || Array.isArray(card.elements)) return value;
  return { ...card, elements: elementsFromLines(card as LegacyCard) };
}

/**
 * A card built from a title and an optional subtitle.
 *
 * The one place that knows how the old stack was laid out, so a card the
 * pipeline creates and a card read from storage arrive at identical positions.
 */
export function cardContent(title: string, subtitle?: string): { kind: "card"; elements: CardTextElement[] } {
  // Motion here, not in `elementsFromLines`: a card built fresh has no retired
  // `entrance` to honour, so it takes the default outright. A card being read
  // from storage does, and `withElementMotion` is what fills it.
  return {
    kind: "card",
    elements: elementsFromLines({ title, subtitle, align: "center" }).map((element) => ({
      ...element,
      motion: { ...defaultCardMotion },
    })),
  };
}

/**
 * Fills an empty card's words, and only an empty one.
 *
 * A new project's HEAD card says whatever the file was called, and its END says
 * "Thanks for watching" — both because nothing better was available at the time
 * the timeline was built. The pass that writes the narration has watched the
 * recording and read the transcript; it knows what the video is about far
 * better than a filename does.
 *
 * Written text is a decision and a generated title is a starting point, so this
 * fills and never overwrites — the same rule the script edits already follow,
 * where re-voicing skips a line whose text still matches. Re-running the
 * analysis therefore rewrites a card nobody has touched and leaves an edited one
 * exactly as it is.
 *
 * Placement, type and motion are left alone: whatever dressed the card, whether
 * a preset or a migration, knows better than this does.
 */
export function fillCardWords<T extends { kind: "card"; elements: CardElement[] }>(
  card: T,
  words: { title?: string; subtitle?: string },
  options: {
    /**
     * A title the build put there itself, which counts as unwritten.
     *
     * The head card is created from the project's name — "recording
     * 1789044643206" off an upload — so by the time the script pass had a real
     * title, the card already had text and this refused to touch it. Nobody
     * wrote that text; the uploader's filename did, and the point of generating
     * a title is to replace exactly that.
     *
     * Matched whole and case-insensitively: a card reading "recording … —
     * final cut" is somebody's edit and stays.
     */
    placeholder?: string;
  } = {},
): T {
  const text = card.elements.filter((element) => element.kind === "text");
  const placeholder = options.placeholder?.trim().toLowerCase();
  const authored = text.filter((element) => {
    if (element.kind !== "text") return false;
    const value = element.text.trim();
    if (!value) return false;
    return !placeholder || value.toLowerCase() !== placeholder;
  });
  if (authored.length > 0) return card;

  const title = words.title?.trim();
  const subtitle = words.subtitle?.trim();
  if (!title) return card;

  const written = cardContent(title, subtitle || undefined).elements;
  // Keep the boxes the card already has where there are any, so a dressed but
  // wordless card stays dressed; fall back to the stack for a card with none.
  const elements = written.map((line, index) => {
    const existing = text[index];
    return existing?.kind === "text" ? { ...existing, text: line.text } : line;
  });

  return { ...card, elements };
}

/** What to call a card in a list: its first line. */
export function cardLabel(content: { kind: string; elements?: CardElement[] }): string {
  if (content.kind !== "card") return "";
  const first = content.elements?.find((element) => element.kind === "text" && element.text.trim());
  return first?.kind === "text" ? first.text : "";
}

/**
 * The shadow, as the CSS that will carry it.
 *
 * Three properties, because the three kinds of element have three different
 * silhouettes and only one of them is its own box.
 *
 *   text              text-shadow    follows the letterforms. A box-shadow on
 *                                    a text element shadows its bounding box,
 *                                    which is a rectangle behind the words.
 *   rect, ellipse     box-shadow     the shape fills its box, and the shadow
 *                                    follows the border radius with it.
 *   image, cover      box-shadow     the image fills the box, so the box is
 *                                    the silhouette.
 *   image, contain    filter         a logo with transparency does not fill
 *                                    its box; a box-shadow would put a
 *                                    rectangle behind a transparent PNG.
 *   line, arrow       filter         a line does not fill its box either.
 *
 * `drop-shadow` is not a fallback here — the browser exporter detects it and
 * precomposites the element so the filter applies to what was actually
 * painted, which is exactly the behaviour a transparent image needs.
 *
 * Nobody picks a property. The kind and the fit decide it, and the panel only
 * ever offers a shadow.
 */
export function cardShadowStyle(element: CardElement): {
  textShadow?: string;
  boxShadow?: string;
  filter?: string;
} {
  const shadow = element.shadow;
  if (!shadow) return {};

  const { x, y, blur } = shadow;
  const colour = rgbaFrom(shadow.color, shadow.opacity);

  if (element.kind === "text") {
    return { textShadow: `${x}px ${y}px ${blur}px ${colour}` };
  }

  // `drop-shadow` takes the same three lengths and a colour, in the same
  // order, which is the reason one stored shape can feed either property.
  const followsAlpha =
    (element.kind === "image" && element.fit === "contain") ||
    (element.kind === "shape" && (element.shape === "line" || element.shape === "arrow"));

  return followsAlpha
    ? { filter: `drop-shadow(${x}px ${y}px ${blur}px ${colour})` }
    : { boxShadow: `${x}px ${y}px ${blur}px ${colour}` };
}

/**
 * A hex colour and an opacity, as one `rgba()`.
 *
 * Separate channels rather than an eight-digit hex, because the exporter's
 * colour matcher looks for `rgba?(...)`, `hsla?(...)`, `#rgb`-through-`#rrggbbaa`
 * or a bare name — and of those, `rgba()` is the one every renderer agrees on.
 */
function rgbaFrom(hex: string, opacity: number): string {
  const value = hex.replace("#", "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value.slice(0, 6);
  const [r, g, b] = [0, 2, 4].map((i) => Number.parseInt(full.slice(i, i + 2), 16) || 0);
  return `rgba(${r}, ${g}, ${b}, ${Number(opacity.toFixed(4))})`;
}
